/**
 * BottomSheet — the phone app's modal surface.
 *
 * Anchored to the bottom rather than centred: on a phone the bottom is where the
 * thumb is, and a centred dialog puts its actions where they cannot be reached
 * one-handed. `pb-safe` keeps the last control clear of the home indicator.
 *
 * Extracted because the date-range picker and the new-booking form were the same
 * shell — overlay, rounded top, header with a title and a close button — written
 * twice. Two copies is where a fixed z-index or safe-area bug gets fixed in one and
 * not the other.
 *
 * The overlay tap is disabled while `busy` so a request in flight cannot be dismissed
 * out from under itself, leaving the caller unsure whether it completed.
 */

import React from 'react';
import { X } from 'lucide-react';

/**
 * @param {{
 *   open: boolean,
 *   title: string,
 *   onClose: () => void,
 *   busy?: boolean,
 *   footer?: React.ReactNode,  rendered pinned below the scrolling body
 *   children: React.ReactNode,
 * }} props
 */
const BottomSheet = ({ open, title, onClose, busy = false, footer, children }) => {
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end">
            <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />

            {/* max-h + flex column so a long form scrolls INSIDE the sheet and the
                footer stays put, rather than the sheet growing past the viewport and
                taking its own submit button off screen. */}
            <div className="relative flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white pb-safe">
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3">
                    <h2 className="text-base font-semibold text-gray-900">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        aria-label="Close"
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                    {children}
                </div>

                {footer && (
                    <div className="shrink-0 border-t border-gray-100 px-4 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BottomSheet;
